/**
 * Device, inbox and delivery-job shapes for trip alerts.
 * Profiles personalise copy and audience; they are not access control.
 */

import * as z from "zod";

export const ALERT_CATEGORIES = [
  "test",
  "briefing",
  "flight_gate",
  "flight_disruption",
  "connection_concern",
  "arrival_transfer",
  "rain_activity",
  "next_destination",
  "official_warning",
  "boat_crossing",
  "pickup_change",
  "pickup_reminder",
  "missing_detail",
  "tdac",
  "check_in_opens",
  "money_due",
  "checkout",
  "tomorrow_move",
  "food_tour",
  "offline_pack",
  "itinerary",
  "budget_exhausted",
] as const;

export type AlertCategory = (typeof ALERT_CATEGORIES)[number];

export const AlertCategorySchema = z.enum(ALERT_CATEGORIES);

export const TRAVELLER_PROFILES = [
  { id: "chris", label: "Chris", householdId: "chris", coordination: true },
  { id: "paul", label: "Paul", householdId: "paul", coordination: false },
  { id: "travelling-chris", label: "Travelling with Chris", householdId: "chris", coordination: false },
  { id: "travelling-paul", label: "Travelling with Paul", householdId: "paul", coordination: false },
] as const;

export type TravellerProfileId = (typeof TRAVELLER_PROFILES)[number]["id"];

export const PushPresetSchema = z.enum(["normal", "essential"]);

export const DevicePreferencesSchema = z.object({
  preset: PushPresetSchema.default("normal"),
  briefingEnabled: z.boolean().default(true),
  briefingTime: z.string().regex(/^\d{2}:\d{2}$/).default("07:30"),
  zoneOverride: z.string().nullable().default(null),
  quietHoursStart: z.string().regex(/^\d{2}:\d{2}$/).default("21:30"),
  quietHoursEnd: z.string().regex(/^\d{2}:\d{2}$/).default("07:00"),
  urgentBypassQuietHours: z.boolean().default(true),
  outstandingTasksAfterTrip: z.boolean().default(false),
});

export type DevicePreferences = z.infer<typeof DevicePreferencesSchema>;

export const SetupStatusSchema = z.enum([
  "unsupported",
  "needs_install",
  "permission_not_requested",
  "blocked",
  "subscription_active",
  "test_sent",
  "test_confirmed",
]);

export type SetupStatus = z.infer<typeof SetupStatusSchema>;

export const PushSubscriptionSchema = z.object({
  endpoint: z.string().url().max(2048),
  keys: z.object({
    p256dh: z.string().min(8).max(512),
    auth: z.string().min(4).max(256),
  }),
});

export const PushDeviceSchema = z.object({
  id: z.string().min(8).max(80),
  endpoint: z.string().max(2048),
  keys: z.object({
    p256dh: z.string(),
    auth: z.string(),
  }),
  sessionRole: z.enum(["family", "owner"]),
  profileId: z.string().nullable().default(null),
  householdId: z.string().nullable().default(null),
  displayLabel: z.string().nullable().default(null),
  preferences: DevicePreferencesSchema,
  setupStatus: SetupStatusSchema,
  dismissedSetup: z.boolean().default(false),
  createdAt: z.string(),
  updatedAt: z.string(),
  lastTestSentAt: z.string().nullable().optional(),
  lastTestConfirmedAt: z.string().nullable().optional(),
  lastPushAcceptedAt: z.string().nullable().optional(),
  lastPushError: z.string().max(400).nullable().optional(),
  lastPushHttpStatus: z.number().int().nullable().optional(),
  userAgent: z.string().max(400).nullable().optional(),
  installedPwa: z.boolean().optional(),
  offlinePackRevision: z.number().int().nullable().optional(),
  offlinePackAt: z.string().nullable().optional(),
  expiresAt: z.string().nullable().optional(),
});

export type PushDevice = z.infer<typeof PushDeviceSchema>;

export const AlertAudienceSchema = z.object({
  allOptedIn: z.boolean().default(false),
  profileIds: z.array(z.string()).default([]),
  householdIds: z.array(z.string()).default([]),
  coordinationOnly: z.boolean().default(false),
  deviceIds: z.array(z.string()).default([]),
  ownerStatusOnly: z.boolean().default(false),
});

export type AlertAudience = z.infer<typeof AlertAudienceSchema>;

export const InboxAlertSchema = z.object({
  id: z.string(),
  topicId: z.string(),
  category: AlertCategorySchema,
  severity: z.enum(["routine", "important", "urgent"]),
  title: z.string(),
  body: z.string(),
  lockTitle: z.string(),
  lockBody: z.string(),
  href: z.string(),
  eventLabel: z.string().nullable().optional(),
  destinationLabel: z.string().nullable().optional(),
  applicableAt: z.string().nullable().optional(),
  source: z.string(),
  sourceUrl: z.string().url().max(2048).nullable().optional(),
  freshnessAt: z.string(),
  changeFrom: z.string().nullable().optional(),
  changeTo: z.string().nullable().optional(),
  supersedesId: z.string().nullable().optional(),
  status: z.enum(["active", "superseded", "resolved"]).default("active"),
  resolutionLabel: z.string().nullable().optional(),
  audience: AlertAudienceSchema,
  deliveryMode: z.enum(["live", "shadow"]).default("live"),
  exemptFromDailyCap: z.boolean().default(false),
  createdAt: z.string(),
  expiresAt: z.string().nullable().optional(),
  seenByDeviceIds: z.array(z.string()).default([]),
});

export type InboxAlert = z.infer<typeof InboxAlertSchema>;

export const DeliveryJobSchema = z.object({
  id: z.string(),
  alertId: z.string(),
  deviceId: z.string(),
  status: z.enum(["queued", "claimed", "accepted", "failed", "expired", "acknowledged"]),
  attempts: z.number().int().nonnegative().default(0),
  nextAttemptAt: z.string(),
  claimedUntil: z.string().nullable().optional(),
  lastError: z.string().nullable().optional(),
  createdAt: z.string(),
  expiresAt: z.string(),
  acceptedAt: z.string().nullable().optional(),
  webPushStatus: z.number().int().nullable().optional(),
});

export type DeliveryJob = z.infer<typeof DeliveryJobSchema>;

export const TripTaskSchema = z.object({
  id: z.string(),
  kind: z.enum(["tdac", "preparation", "other"]),
  title: z.string(),
  responsibleProfileId: z.string().nullable().optional(),
  householdId: z.string().nullable().optional(),
  officialUrl: z.string().nullable().optional(),
  dueDate: z.string().nullable().optional(),
  dueZone: z.string().nullable().optional(),
  completedTravellerKeys: z.array(z.string()).default([]),
  expectedTravellerKeys: z.array(z.string()).default([]),
  confirmationAttached: z.boolean().default(false),
  completedAt: z.string().nullable().optional(),
  coversKey: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
});

export type TripTask = z.infer<typeof TripTaskSchema>;

export const SchedulerHeartbeatSchema = z.object({
  at: z.string(),
  ok: z.boolean(),
  env: z.string(),
  tasksRun: z.array(z.string()).default([]),
  jobsClaimed: z.number().int().nonnegative().default(0),
  jobsAccepted: z.number().int().nonnegative().default(0),
  jobsFailed: z.number().int().nonnegative().default(0),
  skippedPaidFlights: z.boolean().default(false),
  error: z.string().nullable().optional(),
  nextDue: z.record(z.string(), z.string()).optional(),
});

export type SchedulerHeartbeat = z.infer<typeof SchedulerHeartbeatSchema>;

export const PHASE1_LIVE_CATEGORIES: ReadonlySet<AlertCategory> = new Set([
  "test",
  "briefing",
  "pickup_change",
  "pickup_reminder",
  "missing_detail",
  "tdac",
  "money_due",
  "checkout",
  "tomorrow_move",
  "food_tour",
  "offline_pack",
  "itinerary",
  "budget_exhausted",
  "next_destination",
]);

export const ESSENTIAL_CATEGORIES: ReadonlySet<AlertCategory> = new Set([
  "test",
  "briefing",
  "flight_gate",
  "flight_disruption",
  "connection_concern",
  "pickup_change",
  "tdac",
  "money_due",
  "checkout",
  "official_warning",
  "tomorrow_move",
]);

export function defaultPreferences(preset: "normal" | "essential" = "normal"): DevicePreferences {
  return DevicePreferencesSchema.parse({
    preset,
    briefingEnabled: true,
    briefingTime: "07:30",
    zoneOverride: null,
    quietHoursStart: "21:30",
    quietHoursEnd: "07:00",
    urgentBypassQuietHours: true,
    outstandingTasksAfterTrip: false,
  });
}

export function profileById(id: string | null | undefined) {
  return TRAVELLER_PROFILES.find((row) => row.id === id) ?? null;
}
